import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { theme } from '../../../styles/theme';
import { OnboardingStepProps } from '../../../types/onboarding';
import { PersonaConfirmation } from '../../registration';
import { onboardingStorage } from '../../../services/Onboarding';
import { AVAILABLE_PERSONAS, PERSONA_QUESTIONS } from '../../../utils/personaUtils';

const Container = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
`;

const IntroSection = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  padding: 0 1.5rem;
  
  h2 {
    font-size: 2.5rem;
    color: ${theme.colors.text.primary};
    margin-bottom: 1rem;
    background: ${theme.colors.gradients.primary};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
  
  p {
    color: ${theme.colors.text.secondary};
    font-size: 1.15rem;
    max-width: 600px;
    margin: 0 auto;
  }
`;

export const PersonaConfirmationStep: React.FC<OnboardingStepProps> = ({
    data,
    onChange,
    onNext,
    onSkip,
    onBack,
    setNextOverride,
    setBackOverride
}) => {
    // 1. Get Selected Persona from Previous Step
    // We need to look at the 'assessment' step data to see what they picked.
    const assessmentData = onboardingStorage.getStepData('assessment');
    const selectedPersonaId = assessmentData?.selectedPersona;

    const currentPersona = useMemo(() =>
        AVAILABLE_PERSONAS.find(p => p.id === selectedPersonaId),
        [selectedPersonaId]
    );

    // 2. Local state for quiz answers
    const [answers, setAnswers] = useState<Record<string, string>>(data.personaResponses || {});

    // 3. Handle Answer Selection
    const handleAnswer = useCallback((qId: string, val: string) => {
        const newAnswers = { ...answers, [qId]: val };
        setAnswers(newAnswers);

        // Save to THIS step's data
        onChange({
            ...data,
            personaResponses: newAnswers,
            personaConfidence: 85
        }, true);
    }, [answers, data, onChange]);

    // If no persona was selected (e.g. they manually navigated to URL), redirect back
    useEffect(() => {
        if (!selectedPersonaId) {
            // We can't use navigation hooks here easily as they are in the container.
            // But we can render a "Go back" message or rely on the container to handle redirects if we wanted to be robust.
            // For now, let's assume valid flow.
        }
    }, [selectedPersonaId]);

    if (!currentPersona) {
        return (
            <Container>
                <IntroSection>
                    <h2>No Persona Selected</h2>
                    <p>Please go back to the Assessment step to choose your heart persona.</p>
                </IntroSection>
            </Container>
        );
    }

    return (
        <Container>
            <IntroSection>
                <h2>Confirm Your Persona</h2>
                <p>Answer a quick question to help our AI better understand your {currentPersona.name} traits.</p>
            </IntroSection>
            <PersonaConfirmation
                persona={currentPersona}
                questions={PERSONA_QUESTIONS[currentPersona.id] || []}
                onAnswer={handleAnswer}
                onNext={onNext}
                onBack={onBack}
                onSkip={onSkip || (() => { })}
                responses={answers}
                hideNavigation={true}
            />
        </Container>
    );
};
